/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State,
  getAssetPath,
  Method
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { Locale } from "../../common/locale";
import { EntityData } from "../../common/types";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import {
  TreeViewItemModel,
  TreeViewItemModelExtended,
  ChCheckboxCustomEvent
} from "@genexus/chameleon-controls-library";
import { convertActionToTreeViewItem } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/checkbox",
  "components/edit",
  "components/icon",
  "components/tree-view",
  "components/list-box",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

const FILE_ICON = getIconPath({
  category: "gemini-tools",
  name: "file",
  colorType: "on-surface"
});

const MENU_DELETE = getIconPath({
  category: "menus",
  name: "delete",
  colorType: "primary"
});

const CHECKBOX_ALL_CHECKED_VALUE = "allChecked";
const CHECKBOX_ALL_UNCHECKED_VALUE = "allUnchecked";

@Component({
  tag: "gx-ide-open-api-import",
  styleUrl: "open-api-import.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/open-api-import"]
})
export class GxIdeOpenAPIImport {
  #_componentLocale: any;
  #renderedFirstTime = false;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/new-object/shortcuts.json`);

  @Element() el: HTMLGxIdeOpenApiImportElement;

  #chShortcutsEl: HTMLChShortcutsElement;
  #fileInputHiddenEl!: HTMLInputElement;
  #parentEl!: HTMLGxIdeEntitySelectorElement;

  @State() actionListItems: TreeViewItemModel[] = [];
  @State() importingIsInProcess = false;
  @State() operations: ApiOperationData[] = [];
  @State() selectedFile: File;
  @State() selectedOperationForDescription: string;
  @State() selectedOperations: string[] = [];
  @State() selectAllCheckboxValue: string;
  @State() selectAllCheckboxIndeterminate: boolean = false;

  /**
   * Default value for the Module/Folder field.
   */
  @Prop() readonly defaultParent: EntityData;

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Callback invoked when the user wants to cancel the export process.
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback invoked when the user wants to initiate the import process.
   */
  @Prop() readonly importCallback: ImportCallback;

  /**
   * Callback invoked must be loaded the content for an specification API
   */
  @Prop() readonly loadCallback: LoadCallback;

  /**
   * Callback invoked when action is executed on Module/Folder filter (button '...').
   */
  @Prop() readonly selectParentCallback: SelectParentCallback;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  #objectsTreeEl!: HTMLChTreeViewRenderElement;

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  // 9.LOCAL METHODS //
  #cancelImportHandler = () => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };

  #handleImport = async () => {
    await this.importCallback(this.selectedOperations);
  };

  #objectsTreeCheckedItemsChangedHandler = (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    if (event.detail.size === 0) {
      return;
    }
    const allItems = event.detail;
    const checkedItems = [...allItems.values()].filter(
      node => node.item.checked
    );
    this.selectedOperations = checkedItems.map(item => item.item.id);

    // Evaluate "Select All" checkbox state (siguiendo el patrón de kb-manager-import)
    if (allItems.size === checkedItems.length) {
      this.selectAllCheckboxValue = CHECKBOX_ALL_CHECKED_VALUE;
      this.selectAllCheckboxIndeterminate = false;
    } else if (checkedItems.length === 0) {
      this.selectAllCheckboxValue = CHECKBOX_ALL_UNCHECKED_VALUE;
      this.selectAllCheckboxIndeterminate = false;
    } else {
      this.selectAllCheckboxIndeterminate = true;
    }
  };

  #handleSelectAll = (event: ChCheckboxCustomEvent<string> | InputEvent) => {
    if ("detail" in event && this.#objectsTreeEl) {
      const checked =
        (event as ChCheckboxCustomEvent<string>).detail ===
        CHECKBOX_ALL_CHECKED_VALUE;
      // Usar updateAllItemsProperties como en kb-manager-import
      this.#objectsTreeEl.updateAllItemsProperties({ checked: checked });
      this.selectedOperations = checked ? this.operations.map(op => op.id) : [];
      this.selectAllCheckboxValue = checked
        ? CHECKBOX_ALL_CHECKED_VALUE
        : CHECKBOX_ALL_UNCHECKED_VALUE;
      this.selectAllCheckboxIndeterminate = false;
    }
  };

  #objectTreeClickedHandler = (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    this.selectedOperationForDescription = this.operations.filter(
      op => op.id === event.detail[0]?.item.id
    )[0]?.description;
  };

  #removeFileSelection = () => {
    this.selectedFile = null;
    this.#fileInputHiddenEl.value = null;
    this.operations = [];
    this.actionListItems = [];
    this.selectedOperationForDescription = null;
  };

  #selectFileInputChangedHandler = async (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    if (target.files?.length > 0) {
      this.selectedFile = target.files[0];
      this.operations = await this.loadCallback(
        this.selectedFile,
        this.#parentEl.value.id
      );
      this.actionListItems = convertActionToTreeViewItem(this.operations);
    }
  };

  #selectFileInputHandler = () => {
    this.#fileInputHiddenEl.click();
  };

  #renderFooter = (): HTMLElement => {
    return (
      <footer class="dialog-footer dialog-footer-space-between spacing-body-block-end spacing-body-inline">
        <div class="buttons-spacer">
          <ch-checkbox
            id="select-all-checkbox"
            class="checkbox"
            checkedValue={CHECKBOX_ALL_CHECKED_VALUE}
            unCheckedValue={CHECKBOX_ALL_UNCHECKED_VALUE}
            caption={this.#_componentLocale.main.selectAll}
            value={this.selectAllCheckboxValue}
            indeterminate={this.selectAllCheckboxIndeterminate}
            onInput={this.#handleSelectAll}
            disabled={this.importingIsInProcess || !this.operations?.length}
            part="select-all-checkbox"
          ></ch-checkbox>
        </div>
        <div class="buttons-spacer">
          <button
            class="button-secondary"
            slot="footer-end"
            part="button-cancel"
            onClick={this.#cancelImportHandler}
          >
            {this.#_componentLocale.footer.cancel}
          </button>

          <button
            class="button-primary"
            slot="footer-end"
            part="button-confirm"
            onClick={this.#handleImport}
          >
            {this.#_componentLocale.footer.import}
          </button>
        </div>
      </footer>
    );
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section>
          <header
            class="header dialog-header-with-border space-body spacing-body-inline spacing-body-block-start"
            role="banner"
          >
            <div class="field-group header__field-group">
              <div class="import-file-label">
                <div class="field field-block">
                  <label class="label" htmlFor="file-name">
                    {this.#_componentLocale.header.fileNameLabel}
                  </label>
                </div>
              </div>
              <div class="input-file">
                <div class="field input-file">
                  <ch-edit
                    autoFocus
                    id="file-name"
                    class="input file"
                    part="file-name"
                    type="text"
                    readonly
                    value={this.selectedFile?.name}
                    placeholder={
                      this.#_componentLocale.header.fileNamePlaceholder
                    }
                    startImgSrc={FILE_ICON}
                  ></ch-edit>
                  <input
                    hidden
                    type="file"
                    accept=".json, .yaml"
                    onChange={this.#selectFileInputChangedHandler}
                    ref={(el: HTMLInputElement) =>
                      (this.#fileInputHiddenEl = el as HTMLInputElement)
                    }
                  />
                </div>
                <div class="cancel-btn ">
                  <button
                    aria-label={
                      this.#_componentLocale.header.removeFileSelection
                    }
                    title={this.#_componentLocale.header.removeFileSelection}
                    id="reset-all-button"
                    class="button-tertiary button-icon-only"
                    part="reset-all-button"
                    onClick={this.#removeFileSelection}
                  >
                    <ch-image class="icon-m" src={MENU_DELETE}></ch-image>
                  </button>
                </div>
              </div>

              <div class="header__buttons-container">
                <button
                  id="select-file-load-button"
                  class="button-primary"
                  part="select-file-load-button"
                  onClick={this.#selectFileInputHandler}
                  disabled={this.importingIsInProcess}
                >
                  {this.#_componentLocale.header.selectFileButton}
                </button>
              </div>
              <div class="module-folder-label">
                <div class="field field-block">
                  <label class="label">
                    {this.#_componentLocale.header.moduleFolderLabel}
                  </label>
                </div>
              </div>
              <div class="field module-folder-name ">
                <gx-ide-entity-selector
                  value={this.defaultParent}
                  defaultValue={this.defaultParent}
                  selectEntityCallback={this.selectParentCallback}
                  labelPosition="none"
                  ref={(el: HTMLGxIdeEntitySelectorElement) =>
                    (this.#parentEl = el as HTMLGxIdeEntitySelectorElement)
                  }
                  class="module-folder"
                ></gx-ide-entity-selector>
              </div>
            </div>
          </header>

          <main class="main">
            <div class="section-grid">
              <h2
                id="operations-title"
                class="dialog-header dialog-header-with-border dialog-header__operations subtitle-regular-xs text-align-center spacing-body-inline"
              >
                {this.#_componentLocale.main.operations}
              </h2>

              <div
                class="list-wrapper field-group spacing-body-block-start spacing-body-inline scrollable"
                role="region"
                aria-labelledby="operations-title"
              >
                <ch-tree-view-render
                  class="tree-view tree-operations"
                  model={this.actionListItems}
                  show-lines="last"
                  onCheckedItemsChange={
                    this.#objectsTreeCheckedItemsChangedHandler
                  }
                  onSelectedItemsChange={this.#objectTreeClickedHandler}
                  ref={(el: HTMLChTreeViewRenderElement) =>
                    (this.#objectsTreeEl = el as HTMLChTreeViewRenderElement)
                  }
                ></ch-tree-view-render>
              </div>

              <h2
                id="description-title"
                class="dialog-header dialog-header-with-border dialog-header__description subtitle-regular-xs text-align-center spacing-body-inline"
              >
                {this.#_componentLocale.main.procedureDescription}
              </h2>

              <div
                class="procedure-description field-group spacing-body-block-start spacing-body-inline scrollable"
                role="region"
                aria-labelledby="description-title"
              >
                {this.selectedOperationForDescription}
              </div>
            </div>
          </main>
          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type ApiOperationData = {
  id: string;
  name: string;
  description: string;
};
export type SelectionEventDetail = {
  itemId: string;
  itemValue: string;
};

export type CancelCallback = () => Promise<void>;
export type ImportCallback = (operationIds: string[]) => Promise<boolean>;
export type SelectParentCallback = () => Promise<EntityData | undefined>;
export type LoadCallback = (
  source: string | File,
  parentId: string
) => Promise<ApiOperationData[]>;
